import { Fragment, useEffect, useMemo } from 'react';
import {
  createBrowserRouter,
  LoaderFunctionArgs,
  Navigate,
  Outlet,
  RouteObject,
  RouterProvider,
  useNavigate,
} from 'react-router-dom';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { Authenticator, ErrorBoundary, FullScreenLoader, GroupRoute, Toast } from './components';
import { DataSourceActionsPage } from './components/data-sources/routes/actions';
import { DataSourcesDetailsPage } from './components/data-sources/routes/details';
import { CreateCohortPage } from './components/dataset/create-cohort/create-cohort-page';
import { AssignResearcherPage } from './components/organizations/organization-assign-researcher';
import { OrganizationDetails } from './components/organizations/organization-details';
import { InviteResearcherPage } from './components/organizations/organization-invite-researcher';
import { StudyDetails } from './components/studies/drawer/study-details';
import { StudyActionsPage } from './components/studies/routes/actions';
import { DashboardLayout } from './routes/dashboard';
import { indexLoader } from './routes/index-loader';
import { MainRoute } from './routes/main-route';
import { OverviewPage } from './routes/overview';
import { CreateStudyPage } from './routes/overview.create-study';
import { ProfilePage } from './routes/profile._index';
import { ChangePasswordPage } from './routes/profile.change-password';
import { ProfileInformationPage } from './routes/profile.information';
import {
  AboutPage,
  MembersPage,
  ProfilePage as ProfilePageV2,
  SideBar,
  UsageAndMetricsPage,
} from './routes/v2';
import { DashboardLayoutV2 } from './routes/v2/dashboardv2';
import { dataRouter } from './routes/v2/data/router';
import { MainRouteV2 } from './routes/v2/main-routev2';
import { organizationalUnitsRouter } from './routes/v2/organizational-units/router';
import { organizationsRouter } from './routes/v2/organizations/router';
import { studiesRouter } from './routes/v2/studies/router';
import { TranslationKey } from './types/i18next';
import { UserGroup } from './types/users';
import { AuthenticatorV2 } from './components/authenticator/authenticatorv2';
import { CallbackElement } from './routes/callback';
import { CompleteAccountCreationElement } from './routes/v2/complete-account-creation/complete-account-creation';
import { CompleteAccountCreationOverlay } from './routes/v2/complete-account-creation/complete-account-creation-overlay';
import { VerifyPhoneElement } from './routes/v2/complete-account-creation/verify-phone';

const NonExistingRoute = () => {
  const navigate = useNavigate();

  useEffect(() => {
    navigate('/');
  }, [navigate]);

  return <FullScreenLoader />;
};

const createLoader = () => async (_args: LoaderFunctionArgs) => {
  await indexLoader();
  return null;
};

const createRoutes = (accountV2: boolean, launchAccountMenu: boolean): RouteObject[] => {
  const routes: RouteObject[] = [
    {
      path: '/callback',
      element: <CallbackElement />,
      errorElement: <ErrorBoundary />,
    },
    {
      path: 'complete-account-creation',
      element: (
        <CompleteAccountCreationOverlay>
          <Outlet />
        </CompleteAccountCreationOverlay>
      ),
      children: [
        {
          path: '',
          element: <CompleteAccountCreationElement />,
          errorElement: <ErrorBoundary />,
        },
        {
          path: 'verify-phone',
          element: <VerifyPhoneElement />,
          errorElement: <ErrorBoundary />,
        },
      ],
      errorElement: <ErrorBoundary />,
    },
    {
      path: '/',
      errorElement: <ErrorBoundary />,
      element: (
        <Fragment>
          <Toast />
          {accountV2 ? <AuthenticatorV2 /> : <Authenticator />}
        </Fragment>
      ),
      children: [
        {
          path: '',
          loader: createLoader(),
          element: (
            <GroupRoute
              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
              component={accountV2 ? <DashboardLayoutV2 /> : <DashboardLayout />}
            />
          ),
          errorElement: <ErrorBoundary />,
          children: accountV2
            ? [
                {
                  path: '',
                  loader: createLoader(),
                  element: (
                    <GroupRoute
                      groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                      component={<SideBar />}
                    />
                  ),
                  errorElement: <ErrorBoundary />,
                  children: [
                    {
                      path: '',
                      index: true,
                      element: <Navigate to="/account/about" replace />,
                      errorElement: <ErrorBoundary />,
                    },
                    {
                      path: 'overview',
                      element: <Navigate to="/account/about" replace />,
                      errorElement: <ErrorBoundary />,
                    },
                    {
                      path: 'data/create-cohort',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.ADMIN]}
                          component={
                            <MainRouteV2
                              component={<CreateCohortPage />}
                              title="create_cohort_builder"
                            />
                          }
                        />
                      ),
                    },
                    {
                      path: 'account',
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          path: 'about',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                              component={<MainRouteV2 component={<AboutPage />} title="about" />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                        {
                          path: 'profile',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                              component={
                                <GroupRoute
                                  groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                                  component={
                                    <MainRouteV2 component={<ProfilePageV2 />} title="profile" />
                                  }
                                />
                              }
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                        ...(!launchAccountMenu ? organizationsRouter : []),
                        ...(!launchAccountMenu ? organizationalUnitsRouter : []),
                        ...studiesRouter,
                        ...dataRouter,
                      ],
                    },
                    {
                      path: 'administration',
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          path: 'members',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]} // TODO: Change to ADMIN only
                              component={<MembersPage />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                        {
                          path: 'usage-and-metrics',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]} // TODO: Change to ADMIN only
                              component={<UsageAndMetricsPage />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                      ],
                    },
                    {
                      path: '*',
                      element: (
                        <MainRouteV2
                          component={<NonExistingRoute />}
                          title={'404' as TranslationKey}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                    },
                  ],
                },
              ]
            : [
                {
                  path: '',
                  index: true,
                  element: <Navigate to="/overview" />,
                  errorElement: <ErrorBoundary />,
                },
                {
                  path: 'overview',
                  element: (
                    <GroupRoute
                      groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                      component={<MainRoute component={<OverviewPage />} title="overview" />}
                    />
                  ),
                  children: [
                    {
                      path: 'organizations/:organizationId',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                          component={<OrganizationDetails />}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          path: 'create-study',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.ADMIN]}
                              component={<CreateStudyPage />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                        {
                          path: 'assign-researcher',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.ADMIN]}
                              component={<AssignResearcherPage />}
                            />
                          ),
                        },
                        {
                          path: 'invite-researcher',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.ADMIN]}
                              component={<InviteResearcherPage />}
                            />
                          ),
                        },
                      ],
                    },
                    {
                      path: 'studies/:studyId',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                          component={<StudyDetails />}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          path: 'actions',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                              component={<StudyActionsPage />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                      ],
                    },
                    {
                      path: 'data-sources/:dataSourceId',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                          component={<DataSourcesDetailsPage />}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                      children: [
                        {
                          path: 'actions',
                          element: (
                            <GroupRoute
                              groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                              component={<DataSourceActionsPage />}
                            />
                          ),
                          errorElement: <ErrorBoundary />,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'profile',
                  element: (
                    <GroupRoute
                      groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                      component={<MainRoute component={<ProfilePage />} title="profile" />}
                    />
                  ),
                  errorElement: <ErrorBoundary />,
                  children: [
                    {
                      path: 'information',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                          component={<ProfileInformationPage />}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                    },
                    {
                      path: 'change-password',
                      element: (
                        <GroupRoute
                          groupsPermitted={[UserGroup.RESEARCHER, UserGroup.ADMIN]}
                          component={<ChangePasswordPage />}
                        />
                      ),
                      errorElement: <ErrorBoundary />,
                    },
                  ],
                },
                {
                  path: '*',
                  element: (
                    <MainRoute component={<NonExistingRoute />} title={'404' as TranslationKey} />
                  ),
                  errorElement: <ErrorBoundary />,
                },
              ],
        },
      ],
    },
  ];

  return routes;
};

export const AppRouter = () => {
  const { accountV2, launchAccountMenu } = useFlags();

  const router = useMemo(
    () =>
      createBrowserRouter(createRoutes(accountV2, launchAccountMenu), {
        future: {},
      }),
    [accountV2, launchAccountMenu],
  );

  return <RouterProvider router={router} />;
};
